import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
  WalletIcon,
  ChevronDownIcon,
  BellIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  FireIcon,
  QuestionMarkCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  Squares2X2Icon,
  HeartIcon,
  ShoppingBagIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  PlusCircleIcon,
  ChartBarIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../context/WalletContext';

// Public menu - visible to everyone
const publicNavigation = [
  { name: 'Explore', href: '/explore', icon: MagnifyingGlassIcon },
  { name: 'Live Auctions', href: '/live', icon: FireIcon },
  { name: 'How It Works', href: '/how-it-works', icon: QuestionMarkCircleIcon },
  { name: 'Pricing', href: '/pricing', icon: CurrencyDollarIcon },
  { name: 'About', href: '/about', icon: UserGroupIcon },
];

// Authenticated user dropdown menu
const userMenuItems = [
  { name: 'My Dashboard', href: '/dashboard', icon: Squares2X2Icon },
  { name: 'Watchlist', href: '/dashboard/watchlist', icon: HeartIcon },
  { name: 'My Bids', href: '/dashboard/bids', icon: ChartBarIcon },
  { name: 'My Purchases', href: '/dashboard/purchases', icon: ShoppingBagIcon },
  { name: 'My Staking', href: '/dashboard/staking', icon: CurrencyDollarIcon },
  { name: 'Governance', href: '/dashboard/governance', icon: ShieldCheckIcon },
  { name: 'Create Auction', href: '/dashboard/create', icon: PlusCircleIcon },
  { name: 'My Profile', href: '/dashboard/profile', icon: CogIcon },
];

export default function Navbar() {
  const location = useLocation();
  const { address, isConnected, isConnecting, balance, ndhBalance, tier, connectWallet, disconnectWallet } = useWallet();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getTierColor = (t: string) => {
    switch (t) {
      case 'PLATINUM': return 'from-slate-300 to-indigo-400';
      case 'GOLD': return 'from-yellow-400 to-amber-500';
      case 'SILVER': return 'from-slate-400 to-slate-300';
      case 'BRONZE': return 'from-amber-700 to-amber-600';
      default: return 'from-dark-400 to-dark-500';
    }
  };

  const getTierName = (t: string) => {
    switch (t) {
      case 'PLATINUM': return 'Platinum';
      case 'GOLD': return 'Gold';
      case 'SILVER': return 'Silver';
      case 'BRONZE': return 'Bronze';
      default: return 'Basic';
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-dark-950/80 backdrop-blur-xl border-b border-dark-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                <span className="text-white font-bold text-xl font-display">T</span>
              </div>
              <div className="absolute -inset-1 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
            </div>
            <div className="hidden sm:block">
              <span className="text-xl font-bold font-display gradient-text">TEMPLUM</span>
              <span className="text-xl font-bold font-display text-white ml-1">DAO</span>
            </div>
          </Link>

          {/* Desktop Navigation - Menú Público */}
          <div className="hidden lg:flex items-center space-x-1">
            {publicNavigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'text-white'
                      : 'text-dark-300 hover:text-white hover:bg-dark-800'
                  }`}
                >
                  {item.name}
                  {isActive && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute inset-0 bg-dark-800 rounded-lg -z-10"
                      transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Notifications - Solo si está conectado */}
            {isConnected && (
              <Link
                to="/dashboard/notifications"
                className="relative p-2 text-dark-400 hover:text-white transition-colors"
              >
                <BellIcon className="w-6 h-6" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary-500 rounded-full"></span>
              </Link>
            )}

            {/* Wallet Connection / User Menu */}
            {isConnected ? (
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center space-x-3 px-4 py-2 bg-dark-800 border border-dark-700 rounded-xl hover:border-primary-500/50 transition-all duration-200"
                >
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getTierColor(tier)} flex items-center justify-center`}>
                    <UserCircleIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-white">{formatAddress(address!)}</div>
                    <div className="text-xs text-dark-400">{getTierName(tier)}</div>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 text-dark-400 transition-transform duration-200 ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {profileMenuOpen && (
                    <>
                      {/* Overlay para cerrar el menú */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setProfileMenuOpen(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-72 bg-dark-900 border border-dark-700 rounded-xl shadow-2xl overflow-hidden z-20"
                      >
                        {/* Header del menú con info del usuario */}
                        <div className="p-4 bg-gradient-to-r from-dark-800 to-dark-900 border-b border-dark-700">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getTierColor(tier)} flex items-center justify-center`}>
                              <UserCircleIcon className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white">{formatAddress(address!)}</div>
                              <div className={`inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r ${getTierColor(tier)} text-white`}>
                                {getTierName(tier)}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                            <div className="bg-dark-800/50 rounded-lg p-2">
                              <div className="text-xs text-dark-400">Balance</div>
                              <div className="text-sm font-bold text-white">{parseFloat(balance).toFixed(4)} ETH</div>
                            </div>
                            <div className="bg-dark-800/50 rounded-lg p-2">
                              <div className="text-xs text-dark-400">NDH</div>
                              <div className="text-sm font-bold text-primary-400">{parseFloat(ndhBalance).toLocaleString()}</div>
                            </div>
                          </div>
                        </div>

                        {/* Links del menú */}
                        <div className="p-2">
                          {userMenuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.href;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setProfileMenuOpen(false)}
                                className={`flex items-center space-x-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                                  isActive
                                    ? 'bg-primary-500/10 text-primary-400'
                                    : 'text-dark-300 hover:text-white hover:bg-dark-800'
                                }`}
                              >
                                <Icon className="w-5 h-5" />
                                <span>{item.name}</span>
                              </Link>
                            );
                          })}
                        </div>

                        {/* Separador y Desconectar */}
                        <div className="border-t border-dark-700 p-2">
                          <button
                            onClick={() => {
                              disconnectWallet();
                              setProfileMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-dark-800 rounded-lg transition-colors"
                          >
                            <ArrowRightOnRectangleIcon className="w-5 h-5" />
                            <span>Disconnect Wallet</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                disabled={isConnecting}
                className="btn-primary flex items-center space-x-2"
              >
                <WalletIcon className="w-5 h-5" />
                <span>{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-dark-400 hover:text-white"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-dark-900 border-t border-dark-800"
          >
            <div className="px-4 py-4 space-y-2">
              {/* Menú público en mobile */}
              {publicNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-dark-800 text-white'
                        : 'text-dark-300 hover:text-white hover:bg-dark-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              {/* Separador si está conectado */}
              {isConnected && (
                <>
                  <div className="border-t border-dark-700 my-2 pt-2">
                    <p className="px-4 py-2 text-xs text-dark-500 uppercase tracking-wider">My Account</p>
                  </div>
                  {userMenuItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-500/10 text-primary-400'
                            : 'text-dark-300 hover:text-white hover:bg-dark-800'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
